// src/state/SceneContext.js
// Filepath: // src/state/SceneContext.js
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const SceneContext = createContext(null);

const initialScene = { name: "intro", params: {} };

export function SceneProvider({ children }) {
  const [stack, setStack] = useState([initialScene]);
  const stateRef = useRef(new Map()); // sceneKey -> any state blob

  const scene = stack[stack.length - 1];

  const keyFor = (s) => {
    // Stable key per logical screen. Let params override with explicit sceneKey for more control.
    return s?.params?.sceneKey || s?.name;
  };

  const setScene = useCallback((next) => {
    // Backward compatible alias for "replace"; used by existing code
    setStack([next]);
  }, []);

  const replaceScene = useCallback((next) => {
    setStack([next]);
  }, []);

  const pushScene = useCallback((next) => {
    setStack((prev) => [...prev, next]);
  }, []);

  const popScene = useCallback(() => {
    setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const getSceneState = useCallback((s = scene) => {
    const k = keyFor(s);
    return stateRef.current.get(k);
  }, [scene]);

  const setSceneState = useCallback((data, s = scene) => {
    const k = keyFor(s);
    const curr = stateRef.current.get(k) || {};
    stateRef.current.set(k, { ...curr, ...data });
  }, [scene]);

  const value = useMemo(
    () => ({
      scene,
      stack,
      setScene,        // replace
      replaceScene,
      pushScene,
      popScene,
      getSceneState,
      setSceneState,
    }),
    [scene, stack, setScene, replaceScene, pushScene, popScene, getSceneState, setSceneState]
  );

  return <SceneContext.Provider value={value}>{children}</SceneContext.Provider>;
}

export function useScene() {
  const ctx = useContext(SceneContext);
  if (!ctx) throw new Error("useScene must be used within SceneProvider");
  return ctx;
}
